import { useState, useEffect, useRef } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, Check, X, Pencil } from 'lucide-react';

const C = {
  primary: '#1E4E8C', primaryText: 'var(--tone-navy-text)', accent: '#F26B1F',
  slate: 'var(--text-primary)', slateMuted: 'var(--text-muted)',
  border: 'rgba(30,78,140,0.14)',
};

/**
 * Spreadsheet-style inline editor for a single field on the MCI register.
 * Click to edit → Enter/blur saves → Esc cancels. Server rejects invalid
 * values (past dates, unknown priorities) and toast surfaces the error.
 *
 * Supported types: 'text' · 'number' · 'date' · 'select'
 */
export default function InlineEdit({
  value, projectId, mci, field, type = 'text', options = [],
  onSaved, className = '', style = {}, testIdPrefix = 'inline',
}) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value ?? '');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { setVal(value ?? ''); }, [value]);
  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  const save = async () => {
    const oldStr = String(value ?? '');
    const newStr = String(val ?? '');
    if (oldStr === newStr) { setEditing(false); return; }
    setSaving(true);
    try {
      const payload = { [field]: type === 'number' ? Number(val) : val || null };
      await api.patch(
        `/v1/projects/${projectId}/mci/control-tower/entries/${mci}/quick`,
        payload);
      toast.success(`${field} updated`);
      onSaved?.(val);
      setEditing(false);
      window.dispatchEvent(new CustomEvent('mci-register-updated'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Update ${field} failed`);
      setVal(value ?? '');
    }
    setSaving(false);
  };

  const cancel = () => { setVal(value ?? ''); setEditing(false); };

  if (!editing) {
    return (
      <button onClick={(e) => { e.stopPropagation(); setEditing(true); }}
        className={`text-left group ${className}`} style={style}
        data-testid={`${testIdPrefix}-open-${mci}-${field}`}
        title="Click to edit">
        <span>{value || <span style={{ color: C.slateMuted }}>—</span>}</span>
        <Pencil className="w-2.5 h-2.5 inline ml-1 opacity-0 group-hover:opacity-70"
          style={{ color: C.primaryText }} />
      </button>
    );
  }

  const onKey = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); save(); }
    if (e.key === 'Escape') { e.preventDefault(); cancel(); }
  };

  const inputStyle = {
    background: 'var(--tone-amber-bg)', borderColor: C.accent, color: C.slate,
    ...style,
  };

  return (
    <span onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1"
      data-testid={`${testIdPrefix}-editor-${mci}-${field}`}>
      {type === 'select' ? (
        <select ref={inputRef} value={val} onChange={e => setVal(e.target.value)} onKeyDown={onKey}
          onBlur={save} disabled={saving}
          className={`text-[10px] px-1 py-0.5 rounded border ${className}`} style={inputStyle}>
          {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <input ref={inputRef} type={type === 'number' ? 'number' : type === 'date' ? 'date' : 'text'}
          value={val} onChange={e => setVal(e.target.value)} onKeyDown={onKey} onBlur={save}
          disabled={saving}
          className={`text-[10px] px-1 py-0.5 rounded border ${className}`} style={inputStyle} />
      )}
      {saving && <Loader2 className="w-3 h-3 animate-spin" style={{ color: C.accent }} />}
      {!saving && (
        <>
          <button onClick={save} title="Save (Enter)"><Check className="w-3 h-3" style={{ color: '#16A34A' }} /></button>
          <button onClick={cancel} title="Cancel (Esc)"><X className="w-3 h-3" style={{ color: '#EF4444' }} /></button>
        </>
      )}
    </span>
  );
}
